import {
  ACTIVITY_WINDOW_TYPE,
  CORRECT_ITEMS_TO_MASTER,
  DROPPABLE_TYPE,
  FEEDBACK_DISPLAY,
  LEVEL_STAGE,
  NUM_LEVELS,
  ITEMS_PER_LEVEL,
} from '../data/constants';
import gameNames from '../../hub/data/GameIds';

const initialState = {
  feedbackDisplay: FEEDBACK_DISPLAY.NONE,
  sentenceLayout: {},
  // Number of correct Items
  correctStandardItems: 0,
  savedCorrect: 0,
  // For nicely animating the ProgressBar
  progressIndex: 0,
  // For progressing through PRACTICE rounds
  practiceCount: 0,
  // Count for how many Draggables need to be used
  // before displaying the NextButton
  correctMysteryDrops: 0,
  // Simple incrementing count
  draggableProps: [],
  highestLevelEarned: 0,
  currentLevel: 0,
  /**
   * {
   * shouldDisplay: true,
   * text: ""
   * }
   */
  droppableProps: [],
  /**
   * {
   *  droppableType: DROPPABLE_TYPE.PLAIN,
   *  antecedents: [],
   *  text: "",
   *  layout: {}
   * }
   */
  hasCompletedOnboarding: false,
  helpIndex: 0,
};

/**
 * The feedbackReducer produces the response from the user's
 * selected Words
 *
 * Quick Refs:
 * SET_FEEDBACK_DISPLAY
 * UPDATE_WORD_PROPS
 * UPDATE_WORD_LAYOUT
 * UPDATE_SENTENCE_LAYOUT
 * INCREMENT_CORRECT_STANDARD
 * CALCULATE_REWARDS
 * CLEAR_CORRECT_STANDARD
 * UPDATE_DRAGGABLES
 * UPDATE_DROPPABLES
 * UPDATE_DROPPABLE_LAYOUT
 * TOGGLE_DRAGGABLE
 * TOGGLE_DROPPABLE
 * CHANGE_TO_ANTECEDENT
 * CLEAR_COUNTDOWN
 */
const feedbackReducer = (state = initialState, action) => {
  if (action.gameId !== gameNames.UNMASK_THAT) {
    return state;
  }
  switch (action.type) {
    case 'LOAD_NEW_LEVEL':
      return {
        ...state,
        currentLevel: action.level - 1,
        correctStandardItems: 0,
        correctMysteryDrops: 0,
      };
    case 'SET_WINDOW_TYPE':
      if (action.windowType === ACTIVITY_WINDOW_TYPE.TRANSITION) {
        return {
          ...state,
          feedbackDisplay: FEEDBACK_DISPLAY.TRANSITION,
        };
      } if (action.windowType === ACTIVITY_WINDOW_TYPE.PROGRESS) {
        return {
          ...state,
          progressIndex: 0,
          feedbackDisplay: FEEDBACK_DISPLAY.NONE,
        };
      }

      return {
        ...state,
        feedbackDisplay: FEEDBACK_DISPLAY.NONE,
      };
    case 'INCREMENT_STANDARD_INDEX':
    case 'CLEAR_STANDARD_INDEX':
    case 'INCREMENT_PRACTICE_INDEX':
      return {
        ...state,
        feedbackDisplay: FEEDBACK_DISPLAY.NONE,
      };
    case 'CLEAR_PRACTICE_INDEX':
      return {
        ...state,
        practiceCount: 0,
        feedbackDisplay: FEEDBACK_DISPLAY.NONE,
      };
    case 'SET_FEEDBACK_DISPLAY':
      return {
        ...state,
        feedbackDisplay: action.feedbackDisplay,
      };
    case 'SET_LEVEL_STAGE': {
      if (action.levelStage === LEVEL_STAGE.STANDARD) {
        return {
          ...state,
          feedbackDisplay: FEEDBACK_DISPLAY.NONE,
        };
      }
      return {
        ...state,
      };
    }
    case 'UPDATE_SENTENCE_LAYOUT':
      return {
        ...state,
        sentenceLayout: action.sentenceLayout,
      };
    case 'INCREMENT_CORRECT_STANDARD':
      return {
        ...state,
        correctStandardItems: state.correctStandardItems + 1,
      };
    case 'SET_CORRECT_STANDARD':
      return {
        ...state,
        correctStandardItems: action.value,
      };
    case 'INCREMENT_PROGRESS_INDEX':
      return {
        ...state,
        progressIndex: state.progressIndex + 1,
      };
    case 'SET_PRACTICE_COUNT':
      return {
        ...state,
        practiceCount: action.value,
      };
    case 'CALCULATE_REWARDS': {
      let { highestLevelEarned } = state;
      // if mastered in the play we just made
      if (state.correctStandardItems >= CORRECT_ITEMS_TO_MASTER) {
        if (state.currentLevel + 1 < NUM_LEVELS) {
          highestLevelEarned = state.currentLevel + 1;
        } else {
          highestLevelEarned = NUM_LEVELS - 1;
        }
      } else {
        highestLevelEarned = state.currentLevel;
      }
      return {
        ...state,
        highestLevelEarned,
      };
    }
    case 'CLEAR_CORRECT_STANDARD':
      return {
        ...state,
        progressIndex: 0,
        correctStandardItems: 0,
      };
    case 'UPDATE_DRAGGABLES':
      return {
        ...state,
        draggableProps: action.draggableProps,
      };
    case 'UPDATE_DROPPABLES':
      return {
        ...state,
        droppableProps: action.droppableProps,
      };
    case 'UPDATE_DROPPABLE_LAYOUT': {
      const dropProps = state.droppableProps.slice();

      dropProps[action.index] = {
        ...dropProps[action.index],
        layout: action.layout,
      };

      return {
        ...state,
        droppableProps: dropProps,
      };
    }
    case 'TOGGLE_DRAGGABLE': {
      const dragProps = state.draggableProps.slice();

      dragProps[action.index] = {
        ...dragProps[action.index],
        shouldDisplay: !dragProps[action.index].shouldDisplay,
      };

      return {
        ...state,
        draggableProps: dragProps,
      };
    }
    case 'TOGGLE_DROPPABLE': {
      const dropProps = state.droppableProps.slice();
      let { type } = dropProps[action.index];

      // Make an ACTIVE Droppable INACTIVE or an INACTIVE one
      // ACTIVE
      if (type === DROPPABLE_TYPE.ACTIVE) {
        type = DROPPABLE_TYPE.INACTIVE;
      } else if (type === DROPPABLE_TYPE.INACTIVE) {
        type = DROPPABLE_TYPE.ACTIVE;
      }

      dropProps[action.index] = {
        ...dropProps[action.index],
        type,
      };

      return {
        ...state,
        droppableProps: dropProps,
      };
    }
    case 'CHANGE_TO_ANTECEDENT': {
      const dropProps = state.droppableProps.slice();
      const dropProp = { ...dropProps[action.index] };

      let endPunctuation = '';
      // Check to see if there's a punctuation at the end that we should preserve
      if (
        dropProp.text.charAt(
          dropProp.text.length - 1,
        ) === '.' ||
        dropProp.text.charAt(
          dropProp.text.length - 1,
        ) === ',' ||
        dropProp.text.charAt(
          dropProp.text.length - 1,
        ) === '!' ||
        dropProp.text.charAt(
          dropProp.text.length - 1,
        ) === '?'
      ) {
        endPunctuation = dropProp.text.charAt(
          dropProp.text.length - 1,
        );
      }

      // Check to see if antecedent should be capitalized
      // (if the anaphora was capitalized)
      if (
        dropProp.text[0] !==
        dropProp.text[0].toLowerCase()
      ) {
        dropProp.text = action.antecedent.charAt(0).toUpperCase() +
          action.antecedent.slice(1) +
          endPunctuation;
      } else {
        dropProp.text = action.antecedent + endPunctuation;
      }

      // Make the Droppable unusable by switched to PLAIN
      dropProp.type = DROPPABLE_TYPE.PLAIN;
      dropProps[action.index] = dropProp;

      return {
        ...state,
        droppableProps: dropProps,
        correctMysteryDrops: state.correctMysteryDrops + 1,
      };
    }
    case 'CLEAR_COUNTDOWN':
      return {
        ...state,
        correctMysteryDrops: 0,
      };
    case 'INCREMENT_HELP_INDEX':
      return {
        ...state,
        helpIndex: state.helpIndex + 1,
      };
    case 'QUICK_ROUND':
      return {
        ...state,
        correctStandardItems: action.payload ? ITEMS_PER_LEVEL : 0,
      };
    default:
      return state;
  }
};

export default feedbackReducer;
